import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { X, Upload, Check, AlertCircle, FileSpreadsheet, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;

// Maps Excel column headers (trimmed lowercase) → internal field key
const COLUMN_MAP = {
    'full name': 'fullName',
    'email id': 'email',
    'email': 'email',
    'contact number': 'contactNumber',
    'whatsapp number': 'whatsappNumber',
    'designation': 'designation',
    'organisation': 'organization',
    'organization': 'organization',
    'city': 'city',
    'state': 'state',
    'country': 'country',
    'name of medical council': 'medical_council',
    'medical council number': 'medical_council_number',
    'speciality disipline': 'specialty',
    'speciality discipline': 'specialty',
    'specialty/discipline': 'specialty',
    'specialty': 'specialty',
    'registration category': 'registrationCategory',
    'kciamp membership number': 'categoryIdNumber',
    'kciapm membership number': 'categoryIdNumber',
    'membership number': 'categoryIdNumber',
    'amount': 'amountAmount',
};

const DISPLAY_LABELS = {
    fullName: 'Full Name',
    email: 'Email',
    contactNumber: 'Contact Number',
    whatsappNumber: 'WhatsApp Number',
    designation: 'Designation',
    organization: 'Organization',
    city: 'City',
    state: 'State',
    country: 'Country',
    medical_council: 'Medical Council',
    medical_council_number: 'Medical Council No.',
    specialty: 'Specialty',
    registrationCategory: 'Category',
    categoryIdNumber: 'Membership No.',
    amountAmount: 'Amount',
};

const Steps = ({ current }) => {
    const steps = ['Upload File', 'Preview', 'Import'];
    return (
        <div className="flex items-center gap-0 mb-6">
            {steps.map((s, i) => (
                <React.Fragment key={i}>
                    <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                            i < current ? 'bg-indigo-600 border-indigo-600 text-white'
                            : i === current ? 'border-indigo-600 text-indigo-600 bg-white'
                            : 'border-gray-200 text-gray-400 bg-white'
                        }`}>
                            {i < current ? <Check className="w-3.5 h-3.5" /> : i + 1}
                        </div>
                        <span className={`text-xs font-semibold hidden sm:block ${i === current ? 'text-indigo-600' : i < current ? 'text-gray-600' : 'text-gray-300'}`}>{s}</span>
                    </div>
                    {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < current ? 'bg-indigo-600' : 'bg-gray-200'}`} />}
                </React.Fragment>
            ))}
        </div>
    );
};

const BulkRegisterModal = ({ credentials, onClose, onImported }) => {
    const [step, setStep] = useState(0);
    const [file, setFile] = useState(null);
    const [parsedRows, setParsedRows] = useState([]);
    const [unmappedCols, setUnmappedCols] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef();

    const parseFile = useCallback((f) => {
        setFile(f);
        const reader = new FileReader();
        reader.onload = (e) => {
            const wb = XLSX.read(e.target.result, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            // Use header row detection — sheet may have a title row
            const rawRows = XLSX.utils.sheet_to_json(ws, { defval: '' });
            if (rawRows.length === 0) return;

            // The first row might be actual headers if the sheet has a title (like "KAPCON- 2026")
            // Detect: if first row values look like header labels (strings), use it as header row
            const firstRowValues = Object.values(rawRows[0]);
            const looksLikeHeaders = firstRowValues.filter(v => typeof v === 'string' && v.trim().length > 0).length >= 3;

            let dataRows = rawRows;
            let headerRow = null;

            if (looksLikeHeaders) {
                // rawRows[0] is the real header row, rawRows[1+] are data
                headerRow = rawRows[0];
                dataRows = rawRows.slice(1);
            }

            // Build mapped rows
            const mapped = [];
            const unmapped = new Set();

            for (const row of dataRows) {
                const mappedRow = {};
                for (const [col, val] of Object.entries(row)) {
                    // Get the actual column label
                    const colLabel = headerRow ? (headerRow[col] || col) : col;
                    const key = COLUMN_MAP[colLabel.toString().trim().toLowerCase()];
                    if (key) {
                        mappedRow[key] = val;
                    } else {
                        const trimmed = colLabel.toString().trim();
                        if (trimmed && trimmed !== 'Sl. No' && !trimmed.startsWith('__EMPTY')) {
                            unmapped.add(trimmed);
                        }
                    }
                }
                // Only include rows that have at least a name or email
                if (mappedRow.fullName || mappedRow.email) {
                    mapped.push(mappedRow);
                }
            }

            setParsedRows(mapped);
            setUnmappedCols([...unmapped]);
            setStep(1);
        };
        reader.readAsArrayBuffer(f);
    }, []);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) parseFile(f);
    };

    const handleFileInput = (e) => {
        const f = e.target.files[0];
        if (f) parseFile(f);
    };

    const runImport = async () => {
        setImporting(true);
        try {
            const res = await fetch(`${API}/api/custom-events/admin/participants/bulk-register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, participants: parsedRows }),
            });
            const data = await res.json();
            setResult(data);
            setStep(2);
            if (data.success && onImported) onImported();
        } catch (err) {
            setResult({ success: false, message: err.message });
            setStep(2);
        } finally {
            setImporting(false);
        }
    };

    const previewFields = ['fullName', 'email', 'contactNumber', 'registrationCategory', 'organization', 'categoryIdNumber'];

    // Step 0: Upload
    const StepUpload = () => (
        <div>
            <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current.click()}
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-colors ${
                    dragging ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                }`}
            >
                <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-700 mb-1">Drop your Excel file here</p>
                <p className="text-xs text-gray-400">or click to browse · .xlsx, .xls supported</p>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileInput} />
            </div>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-700">
                Columns will be auto-mapped. Columns not recognised will be skipped. Duplicate emails are skipped automatically.
            </div>
        </div>
    );

    // Step 1: Preview
    const StepPreview = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-indigo-700">{parsedRows.length}</p>
                    <p className="text-[10px] text-indigo-500 font-semibold uppercase">Rows Detected</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
                    <p className="text-2xl font-black text-emerald-700">{Object.keys(DISPLAY_LABELS).filter(k => parsedRows[0]?.[k] !== undefined).length}</p>
                    <p className="text-[10px] text-emerald-500 font-semibold uppercase">Fields Mapped</p>
                </div>
            </div>

            {unmappedCols.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-xs font-semibold text-amber-800">Unrecognised columns (will be skipped):</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                            {unmappedCols.map(c => (
                                <span key={c} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-md">{c}</span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-xl px-4 py-2.5 space-y-0.5">
                <p><span className="font-semibold text-gray-700">Registration Date:</span> Today ({new Date().toLocaleDateString()})</p>
                <p><span className="font-semibold text-gray-700">Amount:</span> 0 (if not in file)</p>
                <p><span className="font-semibold text-gray-700">Is Approved:</span> false — requires manual approval</p>
                <p><span className="font-semibold text-gray-700">Payment Status:</span> completed</p>
            </div>

            <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">Preview (first 5 rows)</p>
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                {previewFields.map(f => (
                                    <th key={f} className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">{DISPLAY_LABELS[f]}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {parsedRows.slice(0, 5).map((row, i) => (
                                <tr key={i} className="hover:bg-gray-50">
                                    {previewFields.map(f => (
                                        <td key={f} className="px-3 py-2 text-gray-700 max-w-[140px] truncate">{String(row[f] ?? '')}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    // Step 2: Result
    const StepResult = () => {
        if (importing) return (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
                <RefreshCw className="w-10 h-10 text-indigo-500 animate-spin" />
                <p className="text-sm font-semibold text-gray-600">Registering participants...</p>
            </div>
        );
        if (!result) return null;

        return (
            <div className="space-y-4">
                <div className={`rounded-2xl p-5 text-center ${result.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                    {result.success ? (
                        <>
                            <Check className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                            <p className="text-xl font-black text-emerald-700">{result.created} registered</p>
                            <p className="text-sm text-emerald-600 mt-1">
                                of {result.total} rows · {result.skipped} skipped
                            </p>
                        </>
                    ) : (
                        <>
                            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                            <p className="text-sm font-bold text-red-700">{result.message || 'Import failed'}</p>
                        </>
                    )}
                </div>

                {result.errors?.length > 0 && (
                    <div className="bg-red-50 border border-red-100 rounded-xl p-3">
                        <p className="text-xs font-bold text-red-700 mb-1">{result.errors.length} rows had issues:</p>
                        <div className="space-y-0.5 max-h-36 overflow-y-auto">
                            {result.errors.map((e, i) => (
                                <p key={i} className="text-[10px] text-red-600">Row {e.row}: {e.error}</p>
                            ))}
                        </div>
                    </div>
                )}

                <button onClick={onClose} className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl transition-colors">
                    Done
                </button>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <Upload className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-gray-900">Bulk Register Participants</h2>
                            <p className="text-[10px] text-gray-400">KAPCON 2026 · Conference</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    <Steps current={step} />
                    {step === 0 && <StepUpload />}
                    {step === 1 && <StepPreview />}
                    {step === 2 && <StepResult />}
                </div>

                {/* Footer */}
                {step < 2 && (
                    <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
                        <button
                            onClick={() => setStep(s => Math.max(0, s - 1))}
                            disabled={step === 0}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 disabled:opacity-30 transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" /> Back
                        </button>

                        {step === 1 ? (
                            <button
                                onClick={runImport}
                                disabled={importing || parsedRows.length === 0}
                                className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-colors"
                            >
                                {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                Register {parsedRows.length} Participants
                            </button>
                        ) : (
                            <button
                                onClick={() => setStep(s => s + 1)}
                                disabled={step === 0 || parsedRows.length === 0}
                                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-colors"
                            >
                                Continue <ChevronRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkRegisterModal;
